import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";
import { Plus, Trash2, Save, Star } from "lucide-react";
import { toast } from "sonner";

export default function TestimonialsManager() {
  const [items, setItems] = useState([]);

  const load = () => api.get("/content/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/content/testimonials", {
        name: "New guest", role: "", quote: "Share their words here…",
        image_url: "", rating: 5, enabled: true, order: items.length,
      });
      toast.success("Added");
      load();
    } catch { toast.error("Could not add"); }
  };

  const save = async (item) => {
    try {
      await api.put(`/content/testimonials/${item.id}`, item);
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/content/testimonials/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const upd = (id, field, val) =>
    setItems(items.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  return (
    <div data-testid="testimonials-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Manage</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Testimonials</h1>
          <p className="text-earth-600 mt-2 max-w-xl">
            Reviews from past guests, shown on the public homepage between Amenities and Gallery.
          </p>
        </div>
        <Button
          onClick={add}
          className="bg-sage-500 text-white hover:bg-sage-600 rounded-full"
          data-testid="add-testimonial-btn"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add testimonial
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((t) => (
          <div
            key={t.id}
            className="bg-white rounded-2xl border border-earth-100 p-6 flex flex-col gap-4"
            data-testid={`testimonial-row-${t.id}`}
          >
            <div className="grid grid-cols-12 gap-3 items-start">
              <Input
                className="col-span-7"
                value={t.name}
                onChange={(e) => upd(t.id, "name", e.target.value)}
                placeholder="Guest name"
              />
              <Input
                className="col-span-3"
                value={t.role || ""}
                onChange={(e) => upd(t.id, "role", e.target.value)}
                placeholder="Role / stay type"
              />
              <Input
                type="number"
                className="col-span-2"
                value={t.order}
                onChange={(e) => upd(t.id, "order", Number(e.target.value))}
                placeholder="Order"
              />
            </div>

            <ImagePicker
              label="Avatar (optional)"
              value={t.image_url}
              onChange={(v) => upd(t.id, "image_url", v)}
              testId={`testimonial-img-${t.id}`}
              aspect="aspect-square"
            />

            <Textarea
              rows={4}
              value={t.quote || ""}
              onChange={(e) => upd(t.id, "quote", e.target.value)}
              placeholder="Their words…"
              data-testid={`testimonial-quote-${t.id}`}
            />

            <div className="flex items-center gap-3">
              <span className="text-sm text-earth-600">Stars:</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => upd(t.id, "rating", n)}
                    data-testid={`testimonial-rating-${t.id}-${n}`}
                  >
                    <Star
                      className={`w-5 h-5 ${
                        n <= (t.rating || 0)
                          ? "text-[#FF6B35] fill-[#FF6B35]"
                          : "text-earth-300"
                      }`}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  checked={t.enabled !== false}
                  onCheckedChange={(v) => upd(t.id, "enabled", v)}
                />
                <span className="text-earth-700">
                  {t.enabled !== false ? "Visible" : "Hidden"}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => save(t)}
                  className="bg-earth-900 text-white hover:bg-earth-800 rounded-full"
                  data-testid={`testimonial-save-${t.id}`}
                >
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Save
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => del(t.id)}
                  data-testid={`testimonial-del-${t.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length === 0 && (
        <div className="p-8 text-center text-earth-500 mt-6">No testimonials yet.</div>
      )}
    </div>
  );
}
